<template>
  <table
    class="table table-hover multi-level-table"
    aria-describedby="multi level table"
    :class="getMainClasses()"
  >
    <thead>
      <tr>
        <th
          v-for="(header, idx) in headers" :key="idx"
          :id="`head-${header.key}`"
          :class="getHeaderClasses(header)"
          :colspan="getColSpan(header)"
          @click="onHeaderClick(header)"
        >
          <slot :name="getHeaderSlotName(header)" :header="header">
            {{ header.label }}
          </slot>
          <info-badge
            v-if="header.tooltip"
            :string-key="header.tooltip"
            placement="top"
          />
        </th>
        <th
          id="expander"
          v-if="showExpander"
          class="multi-level-table__expand-collapse-cell"
        />
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="(metadata, idx) in itemsForRender"
        :key="idx"
        :class="getClassForRow(metadata)"
        @click="expandCollapse(metadata)"
      >
        <td
          v-for="field in metadata.level.fields" :key="field.key"
          :class="getFieldClasses(field)"
          :colspan="getColSpan(field)"
        >
          <slot
            :name="getFieldSlotName(metadata, field)"
            :field="field"
            :data="metadata.data"
            :level="metadata.level"
          >
            {{ metadata.data[field.key] }}
          </slot>
        </td>
        <td
          v-if="showExpander"
          class="multi-level-table__expand-collapse-cell"
        >
          <span v-if="showExpandCollapseButton(metadata)"
            class="multi-level-table__expand-collapse-button"
            :class="getExpanderClasses(metadata)"
          />
          <s-loader v-if="metadata.data[loadingChildrenProperty]" />
        </td>
      </tr>
      <tr v-if="!itemsForRender.length && showEmpty">
        <td
          :colspan="rootTotalColspanForEmptyMessage"
        >
          <s-empty-list
            :loading="loading"
          />
        </td>
      </tr>
    </tbody>
  </table>
</template>
<style lang="sass" scoped>
  @import src/styles/defaults/variables
  @import "src/styles/defaults/mobile-first"

  $levelExtraPadding: .5rem
  $maxLevels: 10
  $expand-collapse-cell-width: 2em

  @mixin levelPaddingLeft($level)
    tr.level-#{$level} > td:first-child
      padding-left: $s-table-cell-padding + $level * $levelExtraPadding

  .multi-level-table
    > tbody >
      tr.striped-accent
        background-color: var(--color-table-body-accent-bg)
    &_padding-children > tbody >
      @for $i from 1 through $maxLevels
        @include levelPaddingLeft($i)
    &__expand-collapse-cell
      width: $expand-collapse-cell-width
      min-width: $expand-collapse-cell-width
      max-width: $expand-collapse-cell-width
    &__expand-collapse-button
      cursor: pointer
    > thead,
    > tfoot
      > tr > th.sorting
        cursor: pointer
        &::after
          margin-left: 10px
          content: '\f0dc'
          font-family: 'Font Awesome 5 Free'
          color: var(--font-color-primary)
        &_asc::after
          content: '\f0de'
        &_desc::after
          content: '\f0dd'
    +media-min-sm()
    +media-min-md()
    +media-min-lg()
    +media-min-xl()
</style>

<script>
import InfoBadge from '@/components/icon/InfoBadge';

const DEFAULT_SORT_DIRECTION = 'asc';

/**
 * Tabela Multi-nível para estruturas de dados hierarquicas
 *
 * Tabela básica em HTML com rotinas para renderizar estruturas de dados hierarquicas,
 * tentando combinar o layout final em uma única tabela HTML.
 *
 * A tabela possui um sistema de potencial carga assincrona de dados filhos. Para mais informações,
 * veja a documentação das props abaixo.
 */
/**
 * @event sort-changed Alteração da ordenação.
 * @param {String} event.sortBy Campo em ordenação. Igual a prop sortBy.
 * @param {String} event.sortDirection Direção da ordenação. Igual a prop sortDirection.
 */
export default {
  components: {
    InfoBadge,
  },
  props: {
    /**
     * Cabeçalhos da tabela, em array.
     * @property {String} headers[].key Identificação da coluna.
     * @property {String} headers[].label Texto do cabeçalho.
     * @property {Number} [headers[].colspan=1] Column Span para o cabeçalho. Opcional, padrão 1.
     * @property {Boolean} [headers[].sortable=false] Habilita ordenação. Opcional, padrão false.
     * @property {String} [headers[].slotName] Nome do slot para customização. Se omitido, igual a
     * HEAD_{key}.
     *
     * @example
     *
     * headers = [
     *  {
     *    key: 'id',
     *    label: 'Código',
     *  },
     *  {
     *    key: 'name',
     *    label: 'Nome',
     *    sortable: true,
     *    colspan: 2,
     *    slotName: 'cabecalho_nome',
     *  },
     * ];
     *
     */
    headers: Array,
    /**
     * Definições dos niveis de profundidade da tabela.
     *
     * Cada nível de profundida da hierarquia deve ser um item na array de definições, na mesma
     * ordem da hierarquia.
     * @property {String} [levels[].childrenProperty] Nome da propriedade no registro pai que possui
     * a coleção de registros filhos.
     * @property {Function} [levels[].childrenGetter] Função que faz o carregamento assincrono de
     * registros filhos. Assinaturas:
     * - function(itemPai) => colecaoFilhos
     * - async function(itemPai) => colecaoFilhos
     * - function(itemPai) => Promise<colecaoFilhos>
     * A função definida para childrenGetter não deve atribuir a coleção filha dentro do item pai,
     * apenas retorna-la, pois a própria tabela fará isso já disparando a reatividade do Vue.
     * @property {Array} levels[].fields Definições das colunas do nível.
     * @property {String} levels[].fields[].key Identificação da coluna.
     * @property {String} [levels[].fields[].class] Classe css para a célula da tabela.
     * @property {Number} [levels[].fields[].colspan=1] Column Span da coluna. Opcional, padrão 1.
     * @property {String} [levels[].fields[].slotName] Nome do slot para customização. Se omitido,
     * igual ao caminho de acesso até a propriedade, exemplo: "PropriedadeNoPai",
     * "ColecaoNoPai.PropriedadeNoFilho" ou "ColecaoNoPai.ColecaoNoFilho.PropriedadeNoNeto" e assim
     * por diante.
     *
     * @example
     *
     * levels = [
     *  {
     *    // Primeiro nível, registro pai.
     *    childrenProperty: 'filhos',
     *    fields: [
     *      {
     *        key: 'id',
     *      },
     *      {
     *        key: 'name',
     *        colspan: 2,
     *        slotName: 'nome_pai',
     *      },
     *    ]
     *  },
     *  {
     *    // Segundo nível, registro filho.
     *    childrenProperty: 'brinquedos',
     *    childrenGetter: (filho) => http.get('/brinquedos/' + filho.id),
     *    fields: [
     *      {
     *        key: 'id',
     *      },
     *      {
     *        key: 'description',
     *        colspan: 2,
     *      },
     *    ],
     *  },
     * ];
     *
     */
    levels: Array,
    /**
     * Array de itens/dados da tabela.
     *
     * Os itens devem seguir a estrutura hierarquica definida em "levels".
     *
     * Caso registros "filhos" dependam de carregamento assincrono via "childrenGetter", isso
     * deve ser representado com o uso de nulos. Por exemplo, uma coleção nula indica que ainda não
     * foi carregada. Uma array indica que os dados já foram carregados. Coleções vazias devem
     * ser representadas por arrays vazias uma vez que o nulo indica a falta de carga.
     *
     * Se uma coleção filha for nula, mas "childrenGetter" não tiver sido declarado, internamente
     * a tabela entende que os filhos devem ser uma coleção vazia, substituindo o nulo por array
     * vazia.
     *
     */
    items: Array,
    /**
     * Exibir botão para expandir/retrair registro.
     */
    showExpander: {
      type: Boolean,
      default: true,
    },
    /**
     * Exibir mensagem de lista vazia. Apenas para a raiz, não se aplicando as listas
     * de itens filhos.
     */
    showEmpty: {
      type: Boolean,
      default: true,
    },
    /**
     * Propriedade dentro de um registro pai que indica se o registro está expandido/retraido.
     */
    expandedProperty: {
      type: String,
      default: 'expanded',
    },
    /**
     * Propriedade dentro de um registro pai que indica se está acontecendo uma carga assincrona de
     * registros filhos.
     */
    loadingChildrenProperty: {
      type: String,
      default: 'loadingChildren',
    },
    /**
     * Opção para listrar a tabela. Apenas o nível raiz é listrado. Itens filhos seguem a cor do
     * pai.
     */
    striped: {
      type: Boolean,
      default: true,
    },
    /**
     * Campo para ordenar. Propriedade com two-way bind via event/sync.
     */
    sortBy: {
      type: String,
      required: false,
    },
    /**
     * Direção da ordenação asc/desc. Propriedade com two-way bind via event/sync.
     */
    sortDirection: {
      type: String,
      default: DEFAULT_SORT_DIRECTION,
    },
    /**
     * Desativa o deslocamento de registros filhos.
     * Por padrão, cada nível filho sobre um padding/deslocamento, para sinalizar visualmente a
     * hierarquia.
     */
    noPaddingChildren: {
      type: Boolean,
      default: false,
    },
    /**
     * Informa se está havendo carregamento de dados. Usado no EmptyList, que exibe o loader.
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
    };
  },
  methods: {
    getMainClasses() {
      return {
        'multi-level-table_padding-children': !this.noPaddingChildren,
      };
    },
    getHeaderClasses(header) {
      const sortByThis = this.sortByProxy && this.sortByProxy === header.key;
      return {
        sorting: header.sortable,
        sorting_asc: sortByThis && this.sortDirectionProxy === 'asc',
        sorting_desc: sortByThis && this.sortDirectionProxy === 'desc',
      };
    },
    getExpanderClasses(metadata) {
      return `fa fa-angle-${metadata.data[this.expandedProperty] ? 'up' : 'down'}`;
    },
    iterateAndPushRoot(renderList) {
      if (this.items.length) {
        this.items.forEach((item, idx) => this.pushItem(renderList, item, idx, 0));
      }
    },
    iterateAndPushChildren(renderList, parentMetadata, levelIndex) {
      const { childrenProperty } = this.levels[parentMetadata.levelIndex];
      if (!parentMetadata.data[childrenProperty]) {
        return;
      }

      if (parentMetadata.data[childrenProperty].length) {
        parentMetadata.data[childrenProperty].forEach(item => this.pushItem(
          renderList,
          item,
          parentMetadata.rootItemIndex,
          levelIndex,
        ));
      } else {
        this.pushEmptyItem(
          renderList,
          parentMetadata.rootItemIndex,
          levelIndex,
        );
      }
    },
    pushItem(renderList, item, rootItemIndex, levelIndex) {
      const nextLevelIndex = levelIndex + 1;
      const itemMetadata = this.getItemMetadata(item, rootItemIndex, levelIndex);
      renderList.push(itemMetadata);
      if (item[this.expandedProperty]) {
        this.iterateAndPushChildren(renderList, itemMetadata, nextLevelIndex);
      }
    },
    pushEmptyItem(renderList, rootItemIndex, levelIndex) {
      renderList.push(this.getEmptyMetadata(rootItemIndex, levelIndex));
    },
    getItemMetadata(item, rootItemIndex, levelIndex) {
      return {
        level: this.levels[levelIndex],
        data: item,
        levelIndex,
        rootItemIndex,
      };
    },
    getEmptyMetadata(rootItemIndex, levelIndex) {
      return {
        level: this.getFakeEmptyLevelDef(this.levels[levelIndex]),
        data: {
          message: this.$t('general.empty-list'),
        },
        levelIndex,
        rootItemIndex,
      };
    },
    getFakeEmptyLevelDef(level) {
      if (!level.$fakeEmptyDef) {
      // eslint-disable-next-line no-param-reassign
        level.$fakeEmptyDef = this.createFakeEmptyLevelDef(level);
      }

      return level.$fakeEmptyDef;
    },
    createFakeEmptyLevelDef(level) {
      return {
        fields: [
          {
            key: 'message',
            class: 'text-center',
            colspan: this.getLevelTotalColspan(level),
          },
        ],
      };
    },
    getLevelTotalColspan(level) {
      return level.fields.reduce((count, field) => count + this.getColSpan(field), 0);
    },
    getClassForRow(metadata) {
      return {
        [`level-${metadata.levelIndex}`]: true,
        'striped-accent': this.striped && metadata.rootItemIndex % 2 === 1,
      };
    },
    getFieldClasses(field) {
      return field.class;
    },
    async expandCollapse(metadata) {
      if (this.showExpandCollapseButton(metadata)) {
        this.$set(metadata.data, this.expandedProperty, !metadata.data[this.expandedProperty]);
        await this.loadChildren(metadata);
      }
    },
    async loadChildren(metadata) {
      if (metadata.data[metadata.level.childrenProperty]) {
        return;
      }

      if (metadata.level.childrenGetter) {
        this.$set(metadata.data, this.loadingChildrenProperty, true);
        try {
          const children = await metadata.level.childrenGetter(metadata.data);
          this.$set(metadata.data, metadata.level.childrenProperty, children);
        } finally {
          this.$set(metadata.data, this.loadingChildrenProperty, false);
        }
      } else {
        this.$set(metadata.data, metadata.level.childrenProperty, []);
      }
    },
    showExpandCollapseButton(metadata) {
      return !metadata.data[this.loadingChildrenProperty] && metadata.level.childrenProperty;
    },
    getColSpan(cellInterface) {
      return cellInterface.colspan || 1;
    },
    getHeaderSlotName(header) {
      if (!header.slotName) {
        this.$set(header, 'slotName', `HEAD_${header.key}`);
      }
      return header.slotName;
    },
    getFieldSlotName(metadata, field) {
      if (!field.slotName) {
        this.$set(field,
          'slotName',
          this.levels.slice(0, metadata.levelIndex)
            .map(l => l.childrenProperty)
            .concat(field.key)
            .join('.'));
      }
      return field.slotName;
    },
    onHeaderClick(header) {
      if (!header.sortable) {
        return;
      }

      if (this.sortByProxy === header.key) {
        this.sortDirectionProxy = this.sortDirectionProxy === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortByProxy = header.key;
      }
      this.$emit('sort-changed', {
        sortBy: this.sortByProxy,
        sortDirection: this.sortDirectionProxy,
      });
    },
  },
  computed: {
    itemsForRender() {
      const renderList = [];
      this.iterateAndPushRoot(renderList);
      return renderList;
    },
    sortByProxy: {
      get() {
        return this.sortBy;
      },
      set(value) {
        this.$emit('update:sortBy', value);
        this.sortDirectionProxy = DEFAULT_SORT_DIRECTION;
      },
    },
    sortDirectionProxy: {
      get() {
        return this.sortDirection;
      },
      set(value) {
        this.$emit('update:sortDirection', value);
      },
    },
    rootTotalColspan() {
      return this.getLevelTotalColspan(this.levels[0]);
    },
    rootTotalColspanForEmptyMessage() {
      // Somando a coluna do botão de expandir.
      return this.rootTotalColspan + 1;
    },
  },
};
</script>
